import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getSpend } from '@/api/clarityLens'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { SeasonalityModel, StoreBreakdownModel } from '@/types/clarityLens'

type ChartDataModel = {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}

type BarChartDataModel = {
  data: StoreBreakdownModel[]
}

type LineChartDataModel = {
  data: SeasonalityModel
}

export default function useSpend(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()),
    [startDate, endDate, branches, itemName]
  )
  const loading = !isReady || isFetching

  const [supplierBreakdownChartData, setSupplierBreakdownChartData] =
    useState<ChartDataModel>()
  const [storeBreakdownChartData, setStoreBreakdownChartData] =
    useState<BarChartDataModel>()
  const [seasonalityChartData, setSeasonalityChartData] =
    useState<LineChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const response = await getSpend(branches, startDate, endDate, itemName)

      console.log('response', response)

      // Convert supplier breakdown to stacked bar chart
      const stackedBarChart: ChartDataModel = convertToStackedBar(
        response.supplier_breakdown
      )
      setSupplierBreakdownChartData(stackedBarChart)

      // Convert store breakdown to bar chart
      const barChart: BarChartDataModel = convertToBarChart(
        response.store_breakdown
      )
      setStoreBreakdownChartData(barChart)

      // Convert seasonality to line chart
      const lineChart: LineChartDataModel = convertToLineChart(
        response.seasonality
      )
      setSeasonalityChartData(lineChart)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching sales data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    supplierBreakdownChartData,
    storeBreakdownChartData,
    seasonalityChartData,
    loading,
    refetch,
  }
}

function convertToStackedBar(supplierBreakdown: {
  [key: string]: {
    [key: string]: number
  }
}): ChartDataModel {
  const xAxisNames = Object.keys(supplierBreakdown)

  // Collect all unique supplier names across all dates
  const allSuppliers = new Set<string>()
  xAxisNames.forEach((date) => {
    Object.keys(supplierBreakdown[date]).forEach((supplier) => {
      allSuppliers.add(supplier)
    })
  })

  const seriesNames = Array.from(allSuppliers)

  // Build rawData: each inner array is one supplier's values across all dates
  const rawData: number[][] = seriesNames.map((supplier) =>
    xAxisNames.map((date) => supplierBreakdown[date][supplier] || 0)
  )

  return {
    rawData,
    seriesNames,
    xAxisNames,
  }
}

// Convert store breakdown data to bar chart format
function convertToBarChart(storeBreakdown: {
  [key: string]: number
}): BarChartDataModel {
  const transformedStoreBreakdown = Object.entries(storeBreakdown)
    .map(([branchName, amount]) => ({
      branchName,
      grossSalesAmount: amount,
    }))
    .sort((a, b) => b.grossSalesAmount - a.grossSalesAmount)
    .slice(0, 10)

  return {
    data: transformedStoreBreakdown,
  }
}

// Convert seasonality data to line chart format
function convertToLineChart(seasonality: {
  [key: string]: { [key: string]: number }
}): LineChartDataModel {
  const transformedSeasonality: SeasonalityModel = {}

  Object.entries(seasonality).forEach(([year, monthData]) => {
    // Initialize the array for this year if it doesn't exist
    if (!transformedSeasonality[year]) {
      transformedSeasonality[year] = []
    }

    Object.entries(monthData).forEach(([month, amount]) => {
      transformedSeasonality[year].push({
        month: month,
        grossSalesAmount: amount,
      })
    })
  })

  return {
    data: transformedSeasonality,
  }
}
